import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { dispatcher } from 'dispatcher';
import { pick } from 'ramda';

import { useNavigate } from '@interlayer/js/hooks/navigate';
import { useFindItemMenu } from '@interlayer/js/app/hooks/menu/useMenu';
import { useUserSelector } from '@core/js/store/hooks/useUserSelector';
import { useLoginPopup } from '@interlayer/js/components/popups/LoginPopup/hooks';
import { useRunAtp } from '@interlayer/js/app/components/main/AtpButton/hooks/useRunAtp';
import { useEnterDraw } from '@interlayer/js/app/components/main/EnterDraw/hooks/useEnterDraw';
import { useGameCodeLaunch } from '@interlayer/js/app/components/main/Games/hooks/useGameLaunch';

import { TMenuUserRole } from '@core/js/components/system/App/store/types';

export const useRunSlideAction = (enterDrawOptions: any, slide: any, index: any) => {
    const { pathname } = useLocation();
    const { isLoggedIn, role } = useUserSelector(pick(['isLoggedIn', 'role']));
    const showLoginPopup = useLoginPopup();
    const findItemMenu = useFindItemMenu(true);
    const navigate = useNavigate();
    const runAtp = useRunAtp(slide);
    const handleEnterDraw = useEnterDraw({ ...enterDrawOptions, index, type: 'banner' });
    const launchGame = useGameCodeLaunch(slide?.gameCode);

    return useCallback((content) => {
        if (!content) {
            return;
        }

        const sendBannerPosition = (link = content.linkFromBanner) => dispatcher.dispatch('bannerPosition', {
            page: pathname,
            action: content.linkImageType,
            position: index + 1,
            link
        });

        const openNewWindow = () => window.open(content.linkFromBanner, '_blank', 'noopener,noreferrer');

        const handlerNavigate = () => {
            if (content.isNewWindow) {
                return openNewWindow();
            }

            sendBannerPosition();
            navigate(content.linkFromBanner);
        };

        switch (content.linkImageType) {
            case 'URL':
                if (!isLoggedIn) {
                    const foundItem = findItemMenu(content.linkFromBanner);

                    if (foundItem && !foundItem?.roles.includes(role.toUpperCase() as TMenuUserRole)) {
                        showLoginPopup({ onLogin: handlerNavigate });

                        break;
                    }
                }
                handlerNavigate();
                break;
            case 'ENTER_DRAW':
                handleEnterDraw();
                break;
            case 'GAME_LAUNCH':
                if (!content.gameCode) {
                    return;
                }

                launchGame();
                sendBannerPosition('launchGame');
                break;
            case 'ATP_LAUNCH':
                runAtp();

                break;
            default:
                break;
        }
    }, [pathname, index, navigate, isLoggedIn, handleEnterDraw, launchGame, runAtp, findItemMenu, role, showLoginPopup]);
};
